import { getMonthTwoDigits } from '../../utils/date';
import { RequestStatus } from '../../shared/Requests';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import BillingAiTokenConsumptionApi from '../../api/Billing/BillingAiTokenConsumptionApi';
import { AiTokenUsageTotal } from '../../models/billing/aiToken/AiTokenUsageTotal';
import { AiTokenDailyConsumption } from '../../models/billing/aiToken/AiTokenDailyConsumption';
import { AiTokenBotConsumption } from '../../models/billing/aiToken/AiTokenBotConsumption';
import { BillingMetrics } from '../../shared/BillingMetrics';
import FeatureToggleService from '../../services/FeatureToggleService';

interface AiTokenConsumptionDataContextData {
  totalAiTokenConsumption: AiTokenUsageTotal;
  dailyAiTokenConsumption: AiTokenDailyConsumption[];
  botAiTokenConsumption: AiTokenBotConsumption[];
  containsDataOnSelectedMonth: boolean;
  isAiTokenLoading: boolean;
  consolidatedAiTokenApiRequisition: (dateToSearch: Date) => Promise<void>;
  aiTokenMetricIds: Array<BillingMetrics>;
}

interface Props {
  tenant: any;
}

const initialTotalConsumption = new AiTokenUsageTotal({ items: [] });

export const AiTokenConsumptionDataContext = createContext<AiTokenConsumptionDataContextData>(
  {} as AiTokenConsumptionDataContextData,
);

export const AiTokenConsumptionDataContextProvider: React.FC<Props> = ({ children, tenant }) => {
  const [isAiTokenLoading, setIsAiTokenLoading] = useState(false);
  const [containsDataOnSelectedMonth, setContainsDataOnSelectedMonth] = useState(false);
  const [totalAiTokenConsumption, setTotalAiTokenConsumption] = useState<AiTokenUsageTotal>(initialTotalConsumption);
  const [dailyAiTokenConsumption, setDailyAiTokenConsumption] = useState<AiTokenDailyConsumption[]>([]);
  const [botAiTokenConsumption, setBotAiTokenConsumption] = useState<AiTokenBotConsumption[]>([]);

  const billingAiTokenConsumptionApi = useMemo(() => new BillingAiTokenConsumptionApi(), []);

  const aiTokenMetricIds = useMemo(
    () => (tenant?.subscription || [])
      .filter((item) => item?.metricId === BillingMetrics.aiToken)
      .map((item) => item.metricId),
    [tenant?.subscription],
  );

  const consolidatedAiTokenApiRequisition = useCallback(
    async (dateToSearch: Date): Promise<void> => {
      try {
        setIsAiTokenLoading(true);

        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const storageType = (await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.aiToken)) ? 1 : 0;

        const [total, daily, bot] = await Promise.all([
          billingAiTokenConsumptionApi.getTotalAiTokenConsumption(tenant.info.id, monthString, storageType),
          billingAiTokenConsumptionApi.getDailyAiTokenConsumption(tenant.info.id, monthString, storageType),
          billingAiTokenConsumptionApi.getBotAiTokenConsumption(tenant.info.id, monthString, storageType),
        ]);

        setTotalAiTokenConsumption(total);
        setDailyAiTokenConsumption(daily);
        setBotAiTokenConsumption(bot);
        setContainsDataOnSelectedMonth(billingAiTokenConsumptionApi.getStatus() === RequestStatus.SUCCESS);
      } finally {
        setIsAiTokenLoading(false);
      }
    },
    [tenant.info.id, billingAiTokenConsumptionApi],
  );

  const contextValue = useMemo(
    () => ({
      totalAiTokenConsumption,
      dailyAiTokenConsumption,
      botAiTokenConsumption,
      containsDataOnSelectedMonth,
      isAiTokenLoading,
      consolidatedAiTokenApiRequisition,
      aiTokenMetricIds,
    }),
    [totalAiTokenConsumption, dailyAiTokenConsumption, botAiTokenConsumption, containsDataOnSelectedMonth, isAiTokenLoading, consolidatedAiTokenApiRequisition, aiTokenMetricIds],
  );

  return (
    <AiTokenConsumptionDataContext.Provider value={contextValue}>
      {children}
    </AiTokenConsumptionDataContext.Provider>
  );
};

export function useAiTokenConsumptionDataContext(): AiTokenConsumptionDataContextData {
  const context = useContext(AiTokenConsumptionDataContext);

  if (!context) {
    throw new Error(
      'useAiTokenConsumptionDataContext must be used within an AiTokenConsumptionDataContextProvider',
    );
  }

  return context;
}
